/**
 * Help docs regeneration confirm modal — per docs/specs/help-docs.md §6.
 *
 * Owner-only confirmation for regeneration scope selections.
 * Shows expected cost (from HELP_REGEN_COST_PER_DOC stub) and estimated
 * duration so admins can make an informed kickoff decision.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-21-v3
 */
import { Modal } from '../components/modal';
import { HELP_REGEN_COST_PER_DOC } from '../../lib/fixtures';

type Scope =
    | { kind: 'single'; slug: string; title: string }
    | { kind: 'all'; total: number }
    | { kind: 'stale'; slugs: string[]; total: number };

const fmtMoney = (n: number) => `$${n.toFixed(n < 10 ? 3 : 2)}`;
const scopeMessage = (scope: Scope) =>
    scope.kind === 'single'
        ? scope.title
        : scope.kind === 'all'
          ? 'All docs + full index rebuild.'
          : `${scope.total} stale docs only.`;

export function HelpDocsRegenConfirm({
    scope,
    onClose,
    onConfirm
}: {
    scope: Scope | null;
    onClose: () => void;
    onConfirm: (scope: Scope) => void;
}) {
    if (!scope) return null;

    const docCount = scope.kind === 'single' ? 1 : scope.total;
    const cost = HELP_REGEN_COST_PER_DOC * docCount;
    const duration =
        scope.kind === 'single' ? '~10 sec' : scope.kind === 'stale' ? '~8 min' : '~18 min';
    const isMulti = scope.kind !== 'single';

    const title =
        scope.kind === 'single'
            ? 'Regenerate this doc?'
            : scope.kind === 'stale'
              ? `Regenerate ${docCount} stale docs?`
              : `Regenerate all ${docCount} docs?`;
    const subtitle = scopeMessage(scope);

    return (
        <Modal
            open={!!scope}
            onClose={onClose}
            title={title}
            subtitle={subtitle}
            width={480}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={onClose}>
                        Close
                    </button>
                    <button className="btn btn-primary" onClick={() => onConfirm(scope)}>
                        {isMulti ? `Run regeneration (${docCount})` : 'Run regeneration'}
                    </button>
                </>
            }
        >
            <dl className="kv-list" style={{ marginBottom: 10 }}>
                <dt>Docs affected</dt>
                <dd className="mono">{docCount.toLocaleString()}</dd>
                <dt>Estimated cost</dt>
                <dd className="mono">{fmtMoney(cost)}</dd>
                <dt>Estimated duration</dt>
                <dd>{duration}</dd>
                <dt>Provider</dt>
                <dd>MiniMax abab6-chat (OpenRouter fallback)</dd>
            </dl>
            <p
                style={{
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    margin: 0,
                    padding: 10,
                    background: 'var(--bg-input)',
                    borderRadius: 6
                }}
            >
                {scope.kind === 'single'
                    ? 'Previewing a single-doc regeneration and index-refresh workflow.'
                    : 'Previewing a batch regeneration and index-refresh workflow.'}
            </p>
        </Modal>
    );
}
